import { FormErrorDisplay } from "@/components/form/form-error-display";
import TextFieldWithLabel from "@/components/form/hook-form/textfield-with-label";
import { FilledButton } from "@/components/ui/buttons/FilledButton";
import { AUTH_ACTIONS_LAYOUT_CLASS, AUTH_FORM_LAYOUT_CLASS } from "@/pages/auth-layout";
import { AUTH_CTA_CLASS, AuthScreenLayout } from "@/pages/auth-screen-layout";
import { useResetPassword } from "@/hooks/use-reset-password";
import {
  ResetPasswordVerificationFormValues,
  ResetPasswordVerificationSchema,
} from "@/schemas/reset-password-verification-schema";
import { cn } from "@/utils/cn";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

export default function ResetPasswordVerification() {
  const { handleResetPassword, isLoading } = useResetPassword();
  const initialValues: ResetPasswordVerificationFormValues = {
    code: "",
    password: "",
  };

  const {
    control,
    handleSubmit,
    trigger,
    getValues,
    formState: { errors, isValid },
  } = useForm<ResetPasswordVerificationFormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(ResetPasswordVerificationSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  // Custom blur handler: only validate if field has content
  const handleCodeBlur = () => {
    const codeValue = getValues("code");
    if (codeValue && codeValue.trim() !== "") {
      trigger("code");
    }
  };

  const handlePasswordBlur = () => {
    const passwordValue = getValues("password");
    if (passwordValue && passwordValue.trim() !== "") {
      trigger("password");
    }
  };

  return (
    <AuthScreenLayout
      title="Reset your password"
      subtitle="Enter the code we sent and choose a new password."
    >
      <form
        className={cn(AUTH_FORM_LAYOUT_CLASS, "justify-between")}
        onSubmit={handleSubmit((values) => handleResetPassword(values.code, values.password))}
      >
        <div className="flex flex-col gap-8">
          <TextFieldWithLabel
            control={control}
            name="code"
            label="Code"
            placeholder="Please enter the code"
            type="text"
            autocomplete="one-time-code"
            ariaLabel="Code"
            id="code"
            inputClassName="w-full"
            error={errors.code?.message}
            showErrorMessage={false}
            scrollToOnFocus
            onBlur={handleCodeBlur}
            theme="dark"
          />

          <TextFieldWithLabel
            control={control}
            name="password"
            label="Password"
            placeholder="Please enter your password"
            type="password"
            autocomplete="new-password"
            ariaLabel="Password"
            id="password"
            inputClassName="w-full"
            error={errors.password?.message}
            showErrorMessage={false}
            scrollToOnFocus
            onBlur={handlePasswordBlur}
            theme="dark"
          />
        </div>

        <div className={AUTH_ACTIONS_LAYOUT_CLASS}>
          <FilledButton
            disabled={!isValid || isLoading}
            text={isLoading ? "Resetting..." : "Reset Password"}
            type="submit"
            color="white"
            textCase="normal"
            className={AUTH_CTA_CLASS}
          />

          <FormErrorDisplay formErrors={{}} apiError={null} fieldOrder={["code", "password"]} />
        </div>
      </form>
    </AuthScreenLayout>
  );
}
